import {
    Box,
    Flex,
    IconDelete,
    IconEditBoxOutlined,
    IconReset,
    ImageButton,
    toast,
    Tooltip,
} from '@gmg/gmg-react-components';
import { FC, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { getDeleteUserEvent } from 'src/tracking';
import styled from 'styled-components';
import AppContext from '../../AppContext';
import { useRemoveUser } from '../../graphql/useRemoveUser';
import { useResendInvitation } from '../../graphql/useResendInvitation';
import { getErrorMsgForToast } from '../toastErrorHandler';

interface UserManagementActionProps {
    userId: string;
    orgId: string;
    userFirstName?: string;
    userLastName?: string;
    userEmail?: string;
    hasInvitation: boolean;
    onEditUserIconClick: (userId: string) => void;
}

const ActionButtonWrapper = styled(Flex)`
    cursor: pointer;
    gap: 16px;
    justify-content: flex-end;
`;

const UserManagementActions: FC<UserManagementActionProps> = (props) => {
    const context = useContext(AppContext);
    const { t } = useTranslation();
    const removeUser = useRemoveUser();
    const resendInvitation = useResendInvitation();

    const handleDelete = () => {
        context.trackEvent(getDeleteUserEvent());
        const pendingPromise = removeUser.mutateAsync({ id: props.userId, orgId: props.orgId });

        void toast.promise(pendingPromise, {
            loading: t('Common.Toast.Loading', 'Loading'),
            success: t('UserManagementActions.removeUser.toast_success_desc', 'The user was removed successfully!'),
            error: response => getErrorMsgForToast(response, t),
        });
    };

    const handleResendInvitation = () => {
        const pendingPromise = resendInvitation.mutateAsync({ id: props.userId, orgId: props.orgId });

        void toast.promise(pendingPromise, {
            loading: t('Common.Toast.Loading', 'Loading'),
            success: t('UserManagementActions.resendInvitation.toast_success_desc', 'The invitation was sent successfully!'),
            error: response => getErrorMsgForToast(response, t),
        });
    };

    const getUserNameOrEmailAddress = () => {
        if (!props.userFirstName && !props.userLastName) {
            return props.userEmail;
        }
        return [props.userFirstName, props.userLastName].join(' ');
    };

    const handleResendInvitationIconClick = (ev: any) => {
        ev.stopPropagation();
        context.onShowModal('infoModal', {
            title: t('UserManagementActions.resendInvitation.modalTitle_title', 'Resend Invitation'),
            content:
                <Flex>
                    {/* The additional wrapping of Box in Box is required to place the text directly next to the icon.*/}
                    <Box>
                        <Box>
                            {t('UserManagementActions.resendInvitation.question_desc', 'Are you sure you want to resend the invitation to {{resendUser}}?', {
                                resendUser: getUserNameOrEmailAddress(),
                            })}
                        </Box>
                        <Box className="px10_400_weak">
                            {t('UserManagementActions.resendInvitation.information_desc', 'The user will receive a new invitation email.')}
                        </Box>
                    </Box>
                </Flex>,
            onOkClick: handleResendInvitation,
            btnOkCaption: t('ButtonTitles.action_resend_invitation_btn', 'Resend invitation'),
        });
    };

    const handleDeleteIconClick = (ev: any) => {
        ev.stopPropagation();
        context.onShowModal('infoModal', {
            title: t('UserManagementActions.removeUser.modalTitle_title', 'Remove User'),
            content:
                <Flex>
                    {/* The additional wrapping of Box in Box is required to place the text directly next to the icon.*/}
                    <Box>
                        <Box>
                            {t('UserManagementActions.removeUser.question_desc', 'Are you sure you want to remove this user from the organization?')}
                        </Box>
                        <Box className="px10_400_weak">
                            {t('UserManagementActions.removeUser.information_desc', {
                                defaultValue: '{{removedUser}} will lose all assigned seats and permissions for this organization.',
                                removedUser: getUserNameOrEmailAddress(),
                            })}
                        </Box>
                    </Box>
                </Flex>,
            onOkClick: handleDelete,
            btnOkCaption: t('ButtonTitles.action_delete_btn', 'Delete'),
        });
    };

    return (
        <ActionButtonWrapper className="invisibleSVG">
            {props.hasInvitation && (
                <Tooltip message={t('ButtonTitles.action_resend_invitation_btn', 'Resend invitation')}>
                    <ImageButton
                        icon={<IconReset />}
                        onClick={handleResendInvitationIconClick}
                        aria-label="resend invitation icon"
                    />
                </Tooltip>
            )}
            <Tooltip message={t('ButtonTitles.action_edit_btn')}>
                <ImageButton
                    icon={<IconEditBoxOutlined />}
                    onClick={() => { props.onEditUserIconClick(props.userId); }}
                    aria-label="edit icon"
                />
            </Tooltip>
            <Tooltip message={t('ButtonTitles.action_delete_btn')}>
                <ImageButton
                    icon={<IconDelete />}
                    onClick={handleDeleteIconClick}
                    aria-label="delete icon"
                />
            </Tooltip>
        </ActionButtonWrapper>
    );
};

export default UserManagementActions;